/* eslint-disable react/prop-types */
import {
  Button,
  Col,
  Form,
  Row,
  Spin,
  TreeSelect,
} from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import { getAreas } from 'api/catalogos';
import { getPlazasvistaOptimized } from 'api/catalogos/plazas';
import { formatTreeSelectData, normalizeDataAreas } from 'api/configuraciones/clock/IncidenciasReloj/IncidenciaValidacion';
import { getHistoricosPlazas } from 'api/nominas/historicoPlazas';
import { checkToken } from 'api/utils';
import Select from 'components/Select';
import Table from 'components/Table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import currencyFormatter from 'utils/currencyFormatter';
import exportarExcelDesdeColumnas from 'utils/exportarExcelDesdeColumnas';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import { formatReceived } from 'utils/formatValues';

const renderBooleano = (val) => {
  if (val === true || val === 'True') return 'Sí';
  if (val === false || val === 'False') return 'No';
  return val;
};

export const historicoPlazaPermission = {
  permissionModel: 'historicoplaza',
  catalogsModels: [],
  tabsModels: [],
};

const HistoricoPlazas = ({ permission }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [exportandoTodos, setExportandoTodos] = useState(false);
  const [plazas, setPlazas] = useState([]);
  const [areas, setAreas] = useState([]);
  const [data, setData] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _areas,
        _plazas,
      ] = await Promise.all([
        getAreas({ estados_globales: ESTATUS_AUTORIZADO }),
        getPlazasvistaOptimized(),
      ]);
      if (mounted) {
        const formattedURS = formatTreeSelectData(normalizeDataAreas(_areas));
        setAreas(formattedURS);
        setPlazas(_plazas);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const handleAreaChange = async (areaId) => {
    // Filtrar solo plazas disponibles de esa área
    const plaza = await getPlazasvistaOptimized({ directorio_area_id: areaId });
    setPlazas(plaza);
  };

  const onChangePlaza = async (plazaId) => {
    if (plazaId) {
      const _data = await getHistoricosPlazas({ plaza: plazaId });
      setData(formatReceived(_data));
    } else {
      setData([]);
    }
  };

  const columns = [
    {
      titleText: 'Fecha de Registro',
      dataIndex: 'fecha_de_ejecucion',
      key: 'fecha_de_ejecucion',
      width: 200,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Fecha de Inactivación',
      dataIndex: 'fecha_de_inactivacion',
      key: 'fecha_de_inactivacion',
      width: 200,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Fecha de Activación',
      dataIndex: 'fecha_de_activacion',
      key: 'fecha_de_activacion',
      width: 200,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Fecha Asignación de Plaza',
      dataIndex: 'fecha_de_asignacion',
      key: 'fecha_de_asignacion',
      width: 250,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Fecha Desasignación de Plaza',
      dataIndex: 'fecha_de_desasignacion',
      key: 'fecha_de_desasignacion',
      width: 250,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'No. de Plaza',
      dataIndex: 'plaza',
      key: 'plaza',
      width: 200,
      render: (val) => plazas.find((e) => e.id === val)?.clave,
    },
    {
      titleText: 'No. y Nombre del Empleado Anterior',
      dataIndex: 'empleado_anterior',
      key: 'empleado_anterior',
      width: 350,
    },
    {
      titleText: 'No. y Nombre del Empleado Nuevo',
      dataIndex: 'empleado_nuevo',
      key: 'empleado_nuevo',
      width: 350,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto',
      key: 'puesto',
      width: 250,
    },
    {
      titleText: 'Área',
      dataIndex: 'directorio_de_area',
      key: 'directorio_de_area',
      width: 300,
    },
    {
      titleText: 'Sueldo Mensual',
      dataIndex: 'sueldo_mensual_nominal_asignado',
      key: 'sueldo_mensual_nominal_asignado',
      align: 'right',
      width: 300,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Tipo de Plaza',
      dataIndex: 'tipo_de_plaza',
      key: 'tipo_de_plaza',
      width: 200,
    },
    {
      titleText: 'Tipo de Empleado',
      dataIndex: 'tipo_de_empleado',
      key: 'tipo_de_empleado',
      width: 250,
    },
    {
      titleText: 'Tipo de Empleado Nómina',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 250,
    },
    {
      titleText: 'Proyecto Presupuestal',
      dataIndex: 'proyecto_presupuestal',
      key: 'proyecto_presupuestal',
      width: 250,
    },
    { /// cambiar cuando haya suficientes registros
      titleText: 'Programa Presupuestal',
      dataIndex: 'programa_presupuestal',
      key: 'programa_presupuestal',
      width: 250,
    },
    { /// cambiar cuando haya suficientes registros
      titleText: 'Destino Presupuestal',
      dataIndex: 'destino_presupuestal',
      key: 'destino_presupuestal',
      width: 250,
    },
    {
      titleText: 'Categoría de Puesto',
      dataIndex: 'categoria_de_puesto',
      key: 'categoria_de_puesto',
      width: 250,
    },
    {
      titleText: 'Tabulador General',
      dataIndex: 'tabulador_general',
      key: 'tabulador_general',
      width: 300,
    },
    {
      titleText: 'Desde Sueldo Mensual Tabular',
      dataIndex: 'sueldo_mensual',
      key: 'sueldo_mensual',
      align: 'right',
      width: 250,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Hasta Sueldo Mensual Tabular',
      dataIndex: 'sueldo_mensual_hasta',
      key: 'sueldo_mensual_hasta',
      align: 'right',
      width: 250,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Nivel Salarial Tabular',
      dataIndex: 'nivel_salarial_tabular',
      key: 'nivel_salarial_tabular',
      width: 200,
    },
    {
      titleText: 'Tabulador Nominal',
      dataIndex: 'tabulador_nominal',
      key: 'tabulador_nominal',
      width: 300,
    },
    {
      titleText: 'Estatus de Plaza Anterior',
      dataIndex: 'estatus_plaza_anterior',
      key: 'estatus_plaza_anterior',
      width: 250,
    },
    {
      titleText: 'Estatus de Plaza Nuevo',
      dataIndex: 'estatus_plaza_nuevo',
      key: 'estatus_plaza_nuevo',
      width: 250,
    },
    {
      titleText: 'Clave de Plaza',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
    },
    {
      titleText: 'Descripción de Plaza',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      titleText: 'Disponible',
      dataIndex: 'disponible',
      key: 'disponible',
      width: 150,
      render: renderBooleano,
    },
    {
      titleText: 'Plaza Padre',
      dataIndex: 'plaza_padre',
      key: 'plaza_padre',
      width: 300,
    },
    {
      titleText: 'Tipo Régimen Nómina',
      dataIndex: 'tipo_regimen_nomina',
      key: 'tipo_regimen_nomina',
      width: 200,
    },
    {
      titleText: 'Periodicidad de Pago',
      dataIndex: 'periodicidad_de_pago',
      key: 'periodicidad_de_pago',
      width: 200,
    },
    {
      titleText: 'Tipo de Sueldo',
      dataIndex: 'tipo_de_sueldo',
      key: 'tipo_de_sueldo',
      width: 200,
    },
    {
      titleText: 'Tipo de Función',
      dataIndex: 'tipo_de_funcion',
      key: 'tipo_de_funcion',
      width: 200,
    },
    {
      titleText: 'Tipo de Actividad',
      dataIndex: 'tipo_de_actividad',
      key: 'tipo_de_actividad',
      width: 200,
    },
    {
      titleText: 'Zona de Salario Mínimo',
      dataIndex: 'zona_de_salario_minimo',
      key: 'zona_de_salario_minimo',
      width: 200,
    },
    {
      titleText: 'Tipo de Checado',
      dataIndex: 'tipo_de_checado',
      key: 'tipo_de_checado',
      width: 200,
    },
    {
      titleText: 'Fuentes de Financiamiento Internas',
      dataIndex: 'fuentes_de_financiamiento_internas',
      key: 'fuentes_de_financiamiento_internas',
      width: 350,
    },
    {
      titleText: 'Fuente de Financiamiento',
      dataIndex: 'fuente_financiamiento',
      key: 'fuente_financiamiento',
      width: 250,
    },
    {
      titleText: 'Tiempo de Contratación',
      dataIndex: 'tiempo_de_contratacion',
      key: 'tiempo_de_contratacion',
      width: 200,
    },
    {
      titleText: 'Clase de Contratación',
      dataIndex: 'clase_de_contratacion',
      key: 'clase_de_contratacion',
      width: 200,
    },
    {
      titleText: 'Tipo de Contratación',
      dataIndex: 'tipo_de_contratacion',
      key: 'tipo_de_contratacion',
      width: 200,
    },
    {
      titleText: 'Tipo de Movimiento',
      dataIndex: 'tipo_de_movimiento',
      key: 'tipo_de_movimiento',
      width: 250,
    },
    {
      titleText: 'Área (UR/UO/CDC)',
      dataIndex: 'area',
      key: 'area',
      width: 300,
    },
    {
      titleText: 'Administración de Plaza',
      dataIndex: 'administracion_plaza',
      key: 'administracion_plaza',
      width: 250,
    },
    {
      titleText: 'Entidad',
      dataIndex: 'entidad_nombre',
      key: 'entidad_nombre',
      width: 250,
    },
    {
      titleText: 'Presupuesto',
      dataIndex: 'presupuesto',
      key: 'presupuesto',
      align: 'right',
      width: 200,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Sueldo Mensual del Empleado',
      dataIndex: 'sueldo_mensual_empleado',
      key: 'sueldo_mensual_empleado',
      align: 'right',
      width: 250,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Sueldo Diario Nominal Asignado',
      dataIndex: 'sueldo_diario_nominal_asignado',
      key: 'sueldo_diario_nominal_asignado',
      align: 'right',
      width: 250,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Desde Sueldo Mensual Nominal',
      dataIndex: 'desde_sueldo_mensual_nominal',
      key: 'desde_sueldo_mensual_nominal',
      align: 'right',
      width: 250,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Hasta Sueldo Mensual Nominal',
      dataIndex: 'hasta_sueldo_mensual_nominal',
      key: 'hasta_sueldo_mensual_nominal',
      align: 'right',
      width: 250,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Desde Sueldo Diario Nominal',
      dataIndex: 'desde_sueldo_diario_nominal',
      key: 'desde_sueldo_diario_nominal',
      align: 'right',
      width: 250,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Hasta Sueldo Diario Nominal',
      dataIndex: 'hasta_sueldo_diario_nominal',
      key: 'hasta_sueldo_diario_nominal',
      align: 'right',
      width: 250,
      render: (val) => currencyFormatter(val),
    },
    {
      titleText: 'Jornada',
      dataIndex: 'jornada',
      key: 'jornada',
      width: 200,
    },
    {
      titleText: 'Jornada Detalle',
      dataIndex: 'jornada_detalle',
      key: 'jornada_detalle',
      width: 300,
    },
    {
      titleText: 'Total Horas',
      dataIndex: 'total_horas',
      key: 'total_horas',
      width: 150,
    },
    {
      titleText: 'Total Horas (solo Hrs)',
      dataIndex: 'total_horas_solo_hrs',
      key: 'total_horas_solo_hrs',
      width: 200,
    },
    {
      titleText: 'Descripción Total de Horas',
      dataIndex: 'descripcion_total_horas',
      key: 'descripcion_total_horas',
      width: 250,
    },
    {
      titleText: 'Aplica Reloj Checador',
      dataIndex: 'aplica_reloj_checador',
      key: 'aplica_reloj_checador',
      width: 200,
      render: renderBooleano,
    },
    {
      titleText: 'Aplica Horario Especial',
      dataIndex: 'aplica_horario_especial',
      key: 'aplica_horario_especial',
      width: 200,
      render: renderBooleano,
    },
    {
      titleText: 'Aplicar Reloj Checador',
      dataIndex: 'aplicar_reloj_checador',
      key: 'aplicar_reloj_checador',
      width: 200,
      render: renderBooleano,
    },
    {
      titleText: 'Aplicar Horario Especial',
      dataIndex: 'aplicar_horario_especial',
      key: 'aplicar_horario_especial',
      width: 200,
      render: renderBooleano,
    },
  ];

  const onClickExportarTodos = async () => {
    setExportandoTodos(true);
    const raw = await getHistoricosPlazas();
    const list = raw?.results || raw || [];
    exportarExcelDesdeColumnas(
      formatReceived(list),
      columns,
      'Historico_de_Plazas',
    );
    setExportandoTodos(false);
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          name="general"
        >
          <Row gutter={10}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="directorio_areas"
                label="Área"
                hasFeedback
              >
                <TreeSelect
                  showSearch
                  allowClear
                  treeNodeFilterProp="title"
                  treeData={areas}
                  onChange={handleAreaChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="plaza"
                label="No. de Plaza"
              >
                <Select
                  keyProp="id"
                  labelProp="clave_y_descripcion"
                  dataSource={plazas}
                  onChange={onChangePlaza}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label=" ">
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exportandoTodos}
                  onClick={onClickExportarTodos}
                >
                  Exportar Todos
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Table
          cols={columns}
          data={data}
          allowExportPDF={false}
          permission={permission}
        />
      </Spin>
    </Row>
  );
};

export default HistoricoPlazas;
